import type { AppState } from '@store/types'
import { delay, race, select, take } from 'redux-saga/effects'

import { hasProjectRelations, type ImportedEntitiesStats } from '../utils'

const RELATION_ADD_ACTION = 'PROJECT_INTERNAL_RELATIONS_ADD'
const WAIT_ATTEMPTS = 100
const WAIT_DELAY_MS = 20
const RELATION_TIMEOUT_MS = 3000

export function* waitForDependency({
  id,
  type,
}: {
  id: string
  type: 'section' | 'node' | 'block'
}): Generator<any, void, any> {
  while (true) {
    const state: AppState = yield select((currentState) => currentState)
    if (state.relations.dependencies[id]?.type === type) {
      return
    }

    const { timeout } = yield race({
      nextAdd: take(RELATION_ADD_ACTION),
      timeout: delay(RELATION_TIMEOUT_MS),
    })

    if (timeout) {
      throw new Error(`RELATION_NOT_READY: ${type} ${id}`)
    }
  }
}

export function* waitForBaseProject(): Generator<any, void, any> {
  for (let attempt = 0; attempt < WAIT_ATTEMPTS; attempt += 1) {
    const state: AppState = yield select((currentState) => currentState)
    const hasBaseEntities =
      state.manager.project.tabs.length > 0 &&
      state.manager.project.sections.length > 0 &&
      state.manager.project.colors.length > 0

    if (hasBaseEntities && hasProjectRelations(state)) {
      return
    }

    yield delay(WAIT_DELAY_MS)
  }

  throw new Error('BASE_PROJECT_NOT_READY')
}

export function* waitForProjectEntities({
  expectedSectionCount,
  expectedNodeCount,
  expectedBlockCount,
  expectedTagCount,
}: ImportedEntitiesStats): Generator<any, void, any> {
  for (let attempt = 0; attempt < WAIT_ATTEMPTS; attempt += 1) {
    const state: AppState = yield select((currentState) => currentState)
    const sectionCount = state.manager.project.sections.length
    const nodeCount = state.manager.project.nodes.length
    const blockCount = Object.keys(state.manager.project.blocks).length
    const tagCount = state.manager.project.tags.length
    const hasExpectedEntities =
      sectionCount >= expectedSectionCount &&
      nodeCount >= expectedNodeCount &&
      blockCount >= expectedBlockCount &&
      tagCount >= expectedTagCount

    if (hasExpectedEntities && hasProjectRelations(state)) {
      return
    }

    yield delay(WAIT_DELAY_MS)
  }

  throw new Error('IMPORTED_PROJECT_NOT_READY')
}
